import { SvgIcon } from "@mui/material";
import { green } from "@mui/material/colors";
import { alpha, createTheme, darken, lighten } from "@mui/material/styles";
import { light } from "@mui/material/styles/createPalette";

declare module "@mui/material/styles" {
  interface Palette {
    footer: Palette["primary"]; // Assuming you want to mimic the structure of primary color
    myGray: Palette["primary"];
  }

  interface PaletteOptions {
    footer?: PaletteOptions["primary"]; // Optional customization
    myGray?: PaletteOptions["primary"];
  }
}

// export const othersColor: string = "#333333";
// export const chartColors: string[] = [
//   "#E64B00",
//   "#FF9900",
//   "#3399FF",
//   "#003382",
//   "#7F7F7F",
//   "#669900",
//   "#AD3800",
//   "#B4CC00",
//   "#66CCFF",
//   "#FFCC00",
//   "#800080",
//   "#CC66FF",
// ];

export const StyleguideSavioColors = {
  mainBlue: "#003382",
  blue: "#0040a2",
  lightBlue: "#86a0d2",
  grayBreadcrumb: "#313739",
  darkBlue: "#002359",
  disabledBlue: lighten("#0040a2", 0.8),
  white: "#f9fcff",
  black: "#000000",
  grey: "#b1b1b1",
  lightGrey: "#ced0da",
  lighterGrey: "#e7ecef",
  lightestGrey: "#f2f6f9",
  footerGrey: "#F4F4F4",
  red: "#991111",
  green: "#009900",
  greenCartIcon: "#00963A",
  grayCartIcon: "#D9DDE0",
  badges: {
    cart: "#36EC7D",
  },
  alerts: {
    infoText: "#2e7cec",
    infoBackground: "#d2eaff",
    errorText: "#e28085",
    errorBackground: "#fee1e3",
    successText: "#69c17e",
    successBackground: "#e4ffe9",
    warningText: "#ecd16b",
    warningBackground: "#fff2c7",
  },
  greenOkSnackBar: "#60a006",
  errorBackground: "darkred",
  error: "darkred",
  ok: "#60a006",
  text: {
    black: "#485155",
    grayTitles: "#313739",
  },
};

export const StyleguideSavioFonts = {
  font: "sourcesanspro",
  light: 300,
  normal: 400,
  medium: 500,
  bold: 700,
};

export const MiscSavio = {
  maxWidthSite: 1700,
  maxWidthContainerSite: 1600,
  maxWidth1500: 1500,
  hoverColor: StyleguideSavioColors.blue,
  hoverBackgroundTable: StyleguideSavioColors.mainBlue,
};

const savioTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1024,
      lg: 1280,
      xl: 1920,
    },
  },

  typography: {
    fontFamily: StyleguideSavioFonts.font,
  },

  palette: {
    success: {
      main: "#9BEC00",
      light: "#4caf50",
      dark: "#1b5e20",
    },
    mode: "light",
    primary: {
      main: StyleguideSavioColors.mainBlue,
      contrastText: StyleguideSavioColors.white,
    },
    secondary: {
      main: StyleguideSavioColors.lighterGrey,
      contrastText: StyleguideSavioColors.mainBlue,
      light: StyleguideSavioColors.lightestGrey,
    },

    action: {
      active: "#000000",
    },
    footer: {
      main: StyleguideSavioColors.footerGrey,
      contrastText: StyleguideSavioColors.black,
    },
    myGray: {
      main: StyleguideSavioColors.lightGrey,
      light: StyleguideSavioColors.lighterGrey,
      dark: StyleguideSavioColors.grey,
    },
  },
  components: {
    MuiDialog: {
      styleOverrides: {
        paper: {
          width: "100%",
          //maxWidth: 700,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          backgroundColor: StyleguideSavioColors.mainBlue + " !important",
          color: StyleguideSavioColors.white,
          padding: "9px 9px 6px 20px !important",
          fontSize: 16,
          textTransform: "uppercase",
          alignItems: "center",
          height: 42,

          "+ button": {
            padding: 4,
          },

          button: {
            color: "#fff",
            fontSize: 18.5,
            marginTop: -3,

            svg: {
              fontSize: 19,
            },
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          minWidth: 520,
          color: StyleguideSavioColors.text.black,
          ".MuiList-root": {
            ".MuiMenuItem-root": {
              borderBottom: "1px solid " + StyleguideSavioColors.lightGrey,
              "&:first-of-type": {
                borderTop: "1px solid  " + StyleguideSavioColors.lightGrey,
              },
              "&:hover": {
                backgroundColor: StyleguideSavioColors.white,
                color: StyleguideSavioColors.mainBlue,
              },
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        endAdornment: {
          button: {
            height: "28px !important",
            width: "28px !important",
            marginRight: 5,
            "&:last-child": {
              marginRight: 0,
            },
          },
        },
        popper: {
          " .MuiAutocomplete-listbox": {
            " .MuiAutocomplete-option": {
              fontSize: 14,

              color: StyleguideSavioColors.text.black,

              //option selezionati
              "&[aria-selected='true']": {
                backgroundColor: StyleguideSavioColors.white,
              },
            },

            ".Mui-focused": {
              backgroundColor: StyleguideSavioColors.white,
            },
          },
        },

        root: {
          boxShadow: "none",

          ".MuiInputBase-root": {
            height: "34px !important",
          },

          ".MuiOutlinedInput-root": {
            padding: 3,
            paddingTop: 2,
            paddingBottom: 2,

            input: {
              fontSize: 14,
            },

            ".MuiOutlinedInput-notchedOutline": {
              borderColor: StyleguideSavioColors.grey,
            },
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
            backgroundColor: StyleguideSavioColors.white,
            width: 13,
            height: 13,
          },
          "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
            borderRadius: 20,
            backgroundColor: StyleguideSavioColors.lightGrey,
            border: "solid 3px " + StyleguideSavioColors.white,
            cursor: "pointer",
          },
          "&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus":
            {
              backgroundColor: StyleguideSavioColors.lightGrey,
            },
          "&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active":
            {
              backgroundColor: StyleguideSavioColors.lightGrey,
            },
          "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover":
            {
              backgroundColor: darken(StyleguideSavioColors.lightGrey, 0.1),
            },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          th: {
            //backgroundColor: StyleguideSavioColors.lightestGrey,
            textTransform: "uppercase",
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 700,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          position: "relative",

          "&.MuiTableRow-head": {
            "&:hover": {
              "th.MuiTableCell-head": {
                backgroundColor: "#fff !important",
              },
            },
          },

          "&:hover:not(.skeleton .MuiTableRow-root:hover)": {
            backgroundColor: "#fff !important",

            td: {
              textDecoration: "underline",

              color: StyleguideSavioColors.blue,

              " .MuiTypography-root": {
                color: StyleguideSavioColors.blue,
              },

              "&:first-of-type": {
                "&:before": {
                  display: "block",
                  content: "''",
                  width: 3,
                  height: "100%",
                  backgroundColor: StyleguideSavioColors.blue,
                  left: 0,
                  position: "absolute",
                  zIndex: 1,
                  top: 0,
                },
              },
            },
          },

          "&.selected:hover": {
            backgroundColor: StyleguideSavioColors.mainBlue + " !important",
            td: {
              "&:before": {
                content: "none",
              },
              "&.MuiTableCell-root": { textDecoration: "none !important" },
            },
          },
        },
      },
    },

    MuiTableBody: {
      styleOverrides: {
        root: {
          tr: {
            height: 50,
          },
          td: {
            paddingTop: 2,
            paddingBottom: 2,
            color: StyleguideSavioColors.text.black,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          textTransform: "uppercase",
        },
      },
    },

    MuiFab: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: StyleguideSavioColors.blue,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: 14,
          fontWeight: StyleguideSavioFonts.bold,
          color: StyleguideSavioColors.mainBlue,

          "&.MuiInputLabel-outlined": {
            transform: "translate(13px, 14px) scale(1)",

            "&.MuiInputLabel-shrink": {
              color: StyleguideSavioColors.mainBlue,
              transform: "translate(0, -9px) scale(1)",

              "+ .MuiOutlinedInput-root": {
                "fieldset legend ": { fontSize: "1em !important" },
              },
            },
          },

          "&.MuiInputLabel-standard": {
            transform: "translate(0px, 20px) scale(1)",
            "&.MuiInputLabel-shrink": {
              color: StyleguideSavioColors.mainBlue,
              transform: "translate(0px, 0px) scale(1)",

              "+ .MuiOutlinedInput-root": {
                "fieldset legend ": { fontSize: "1em !important" },
              },
            },
          },
        },
      },
    },

    MuiPopper: {
      styleOverrides: {
        root: {
          ".MuiPickersLayout-root": {
            ".MuiDayCalendar-root": {
              //qua sono dentro il calendario

              "> .MuiDayCalendar-header": {
                span: {
                  color: StyleguideSavioColors.mainBlue,
                },
              },

              ".MuiDayCalendar-monthContainer": {
                "button.MuiPickersDay-today": {
                  borderColor: StyleguideSavioColors.mainBlue,
                },
              },

              ".MuiPickersDay-root": {
                "&.Mui-selected": {
                  "&:hover": {
                    backgroundColor: StyleguideSavioColors.blue,
                  },
                },
              },
            },
            ".MuiYearCalendar-root": {
              "button.Mui-selected": {
                "&:hover": {
                  backgroundColor: StyleguideSavioColors.blue,
                },
              },
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          "&.MuiPaper-root:hover": {
            textDecoration: "underline",
            backgroundColor: "#fff",

            "&:before": {
              display: "block",
              content: "''",
              width: 3,
              height: "100%",
              backgroundColor: StyleguideSavioColors.blue,
              left: 0,
              position: "absolute",
            },
          },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          button: {
            color: StyleguideSavioColors.mainBlue,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          "&.MuiPopover-paper": {
            color: StyleguideSavioColors.text.black,
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: 0,

          "> .MuiPaper-root": {
            boxShadow: "none",
          },

          ".MuiTable-root": {
            ".MuiTableCell-root": {
              paddingTop: 10,
              paddingBottom: 10,
            },
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          marginTop: 40 + "px !important",
          marginBottom: 0,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          "&.information": {
            ".MuiPaper-root": {
              backgroundColor: StyleguideSavioColors.white,
              color: StyleguideSavioColors.text.black,
              boxShadow: "0px 4px 14px 0px rgba(0,0,0,0.5)",
              fontWeight: StyleguideSavioFonts.bold,
              borderLeft: "3px solid " + StyleguideSavioColors.blue,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              paddingRight: 32,

              ".MuiTypography-root": {
                fontSize: 14,
              },
            },
          },
          "> .MuiPaper-root": {
            "> ul.MuiList-root": {
              "> li.MuiButtonBase-root.MuiMenuItem-root": {
                fontSize: 14,

                "&:hover:not(.Mui-selected)": {
                  color: StyleguideSavioColors.blue,
                  textDecoration: "underline",
                  backgroundColor: "#fff",

                  "&:before": {
                    display: "block",
                    content: "''",
                    width: 3,
                    height: "100%",
                    backgroundColor: StyleguideSavioColors.blue,
                    left: 0,
                    position: "absolute",
                    zIndex: 1,
                  },
                },

                "&.Mui-selected": {
                  backgroundColor: StyleguideSavioColors.mainBlue,
                  color: "#fff",
                },
              },
            },
          },
        },

        paper: {
          borderRadius: 0,
          "> .MuiList-padding": {
            padding: 0,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          marginLeft: 2,
          marginRight: 2,
          boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.2)",

          minHeight: 60,
          "&.Mui-expanded": {
            minHeight: 60,
          },

          ".MuiAccordionSummary-content": {
            marginTop: 8 + "px !important",
            marginBottom: 8 + "px !important",
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          "&.MuiSwitch-switchBase.Mui-checked": {
            color: MiscSavio.hoverColor,
            "+ .MuiSwitch-track": {
              backgroundColor: MiscSavio.hoverColor,
            },
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          ".MuiSwitch-track": {
            backgroundColor: StyleguideSavioColors.lightGrey,
            opacity: 1,
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: StyleguideSavioColors.text.black,

          ".MuiInputLabel-root": {
            top: -2,
          },

          ".MuiOutlinedInput-input": { paddingTop: 11, paddingBottom: 11 },
          ".MuiSelect-select.MuiSelect-outlined": {
            paddingTop: 8,
            paddingBottom: 8,
          },
          ".MuiOutlinedInput-notchedOutline": {
            borderColor: StyleguideSavioColors.grey,
          },

          fieldset: {
            "&.MuiOutlinedInput-notchedOutline legend": {
              marginLeft: -20,
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          "> .MuiSelect-standard": {
            fontSize: 12,
            
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          //padding: "10px 8px 6px 0px",
          color: StyleguideSavioColors.text.black,
          "&::-webkit-input-placeholder": {
            opacity: 1,
            fontSize: 14,
          },
        },
        multiline: {
          "&:hover": {
            ".MuiOutlinedInput-notchedOutline": {
              borderColor: StyleguideSavioColors.text.black + " !important",
            },
          },
          "&.Mui-focused": {
            ".MuiOutlinedInput-notchedOutline": {
              borderWidth: "1px !important",
              borderColor: StyleguideSavioColors.text.black + " !important",
            },
          },
        },
        root: {
          ".MuiInputBase-inputMultiline": {
            padding: "0 !important",
          },

          "> .MuiSelect-select": {
            position: "relative",
            zIndex: 1,
          },
          "> svg": {
            position: "absolute",
            zIndex: 0,
            right: 5,
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          ":before": {
            borderColor: StyleguideSavioColors.grey + " !important",
          },

          "> .MuiInput-input:not(.MuiSelect-select)": {
            paddingBottom: 8,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          /*
          ".MuiOutlinedInput-notchedOutline": {
            borderColor: StyleguideSavioColors.blue,
          },
          */
          "> .MuiInputLabel-shrink": {
            "+ .MuiOutlinedInput-root": {
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: StyleguideSavioColors.blue,
              },
            },
          },

          ...(ownerState.variant === "standard" && {
            ".MuiInput-root": {
              fontSize: 14,
              color: StyleguideSavioColors.text.black,
            },
          }),
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          paddingTop: 13,
          paddingBottom: 13,
          borderColor: StyleguideSavioColors.lightGrey,
          paddingLeft: 8,
          paddingRight: 8,

          "&.MuiTableCell-head": {
            paddingLeft: 8,
            backgroundColor: "#fff",
            color: StyleguideSavioColors.mainBlue,
            fontWeight: StyleguideSavioFonts.bold,
            borderBottom: "1px solid " + StyleguideSavioColors.mainBlue,
            fontSize: 14,
            paddingTop: 9,
            paddingBottom: 9,
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          maxWidth: MiscSavio.maxWidthContainerSite - 100 + "px !important",
        },
      },
    },
    MuiCollapse: {
      styleOverrides: {
        root: {
          marginTop: 5,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          paddingBottom: 4,

          ...(ownerState.size === "large" && { height: 40 }),
          ...(ownerState.size === "medium" && { height: 35 }),
          ...(ownerState.size === "small" && { height: 30 }),

          ...(ownerState.color === "primary" &&
            ownerState.variant === "contained" && {
              "&:hover": {
                backgroundColor: StyleguideSavioColors.blue,
              },
            }),

          ...(ownerState.color === "secondary" &&
            ownerState.variant === "contained" && {
              backgroundColor: "#fff",

              "&:not(.Mui-disabled)": {
                border: "1px solid" + StyleguideSavioColors.blue,
              },

              "&:hover": {
                backgroundColor: StyleguideSavioColors.mainBlue,
                color: StyleguideSavioColors.white,
              },
            }),
        }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          "&.Mui-selected": { color: StyleguideSavioColors.blue },
        },
      },
    },

    MuiToggleButtonGroup: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.size === "large" && {
            ".MuiToggleButtonGroup-grouped": {
              padding: "8px 22px 4px",
            },
          }),
          ...(ownerState.size === "medium" && {
            ".MuiToggleButtonGroup-grouped": {
              padding: "6px 16px 4px",
            },
          }),
          ...(ownerState.size === "small" && {
            ".MuiToggleButtonGroup-grouped": {
              padding: "4px 10px",
              lineHeight: "20px",
            },
          }),

          button: {
            "&.MuiToggleButton-standard": {
              backgroundColor: "#fff",
              color: StyleguideSavioColors.mainBlue,
              borderColor: StyleguideSavioColors.mainBlue,

              "&:hover": {
                backgroundColor: StyleguideSavioColors.mainBlue,
                color: "#fff",
              },

              "&.Mui-selected": {
                color: "#fff",
                backgroundColor: StyleguideSavioColors.mainBlue,
                "&:hover": {
                  backgroundColor: StyleguideSavioColors.blue,
                },
              },
              "&.Mui-disabled": {
                color: "rgba(0, 0, 0, 0.26)",
                backgroundColor: "rgba(0, 0, 0, 0.12)",
              },
            },
            "&.MuiToggleButton-secondary": {
              backgroundColor: StyleguideSavioColors.mainBlue,
              color: "#fff",
              borderColor: StyleguideSavioColors.mainBlue,

              "&:hover": {
                backgroundColor: StyleguideSavioColors.blue,
              },

              "&.Mui-selected": {
                color: StyleguideSavioColors.mainBlue,
                backgroundColor: "#fff",

                "&:hover": {
                  backgroundColor: StyleguideSavioColors.mainBlue,
                  color: "#fff",
                },
              },
              "&.Mui-disabled": {
                color: "rgba(0, 0, 0, 0.26)",
                backgroundColor: "rgba(0, 0, 0, 0.12)",
              },
            },
          },
        }),
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: StyleguideSavioColors.white,
          color: StyleguideSavioColors.text.black,
          boxShadow: "0px 4px 14px 0px rgba(0,0,0,0.5)",
          fontWeight: StyleguideSavioFonts.bold,
          borderLeft: "3px solid " + StyleguideSavioColors.blue,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          maxWidth: "500px !important",

          ".MuiTableRow-root:hover": {
            backgroundColor: "inherit !important",
            td: {
              textDecoration: "none",
              "&:before": {
                display: "none",
              },

              p: { color: StyleguideSavioColors.text.black },
            },
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ".MuiBadge-badge": {
            fontSize: 13,
            color: "#000",
            fontWeight: StyleguideSavioFonts.bold,
            ...(ownerState.color === "info" && {
              backgroundColor: StyleguideSavioColors.badges.cart,
            }),
          },
        }),
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          boxShadow: "0px 4px 14px 0px rgba(0,0,0,0.5)",
          borderRadius: 6,
          color: "#636c74",
          textAlign: "left",

          ".MuiIconButton-root": {
            color: "#636c74",
            marginLeft: 15,
            marginTop: 4,
            svg: {
              fontSize: "14px",
            },
          },

          "&.MuiAlert-standard": {
            "&.MuiAlert-colorInfo": {
              border: "1px solid " + StyleguideSavioColors.alerts.infoText,
              backgroundColor: StyleguideSavioColors.alerts.infoBackground,
              h2: {
                color: StyleguideSavioColors.alerts.infoText,
              },
            },
            "&.MuiAlert-colorSuccess": {
              border: "1px solid " + StyleguideSavioColors.alerts.successText,
              backgroundColor: StyleguideSavioColors.alerts.successBackground,
              h2: {
                color: StyleguideSavioColors.alerts.successText,
              },
            },
            "&.MuiAlert-colorError": {
              border: "1px solid " + StyleguideSavioColors.alerts.errorText,
              backgroundColor: StyleguideSavioColors.alerts.errorBackground,
              h2: {
                color: StyleguideSavioColors.alerts.errorText,
              },
            },
            "&.MuiAlert-colorWarning": {
              border: "1px solid " + StyleguideSavioColors.alerts.warningText,
              backgroundColor: StyleguideSavioColors.alerts.warningBackground,
              h2: {
                color: StyleguideSavioColors.alerts.warningText,
              },
            },
          },
          ".MuiAlert-message": {
            alignItems: "flex-start",
          },
          "text-message": {
            display: "flex",
            flexDirection: "column",
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: StyleguideSavioColors.text.black,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          color: StyleguideSavioColors.text.black,
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          ".MuiAlert-root": {
            minWidth: 400,
            ".MuiAlert-message": {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              width: "100%",
            },
          },
        },
      },
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    footer: {
      main: "red",
      dark: "red",
    },
  },
});

//export const drawerColor: string = "#FFFFFF";
// const defaultOptions = {
//   components: {
//     MuiAppBar: {
//       styleOverrides: {
//         colorPrimary: {
//           backgroundColor: StyleguideVandewieleColors.primary.blue,
//           color: "#FFFFFF",
//         },
//       },
//     },
//     MuiDrawer: {
//       styleOverrides: {
//         paper: {
//           backgroundColor: drawerColor,
//           color: StyleguideVandewieleColors.primary.blue,
//         },
//       },
//     },
//   },
//   palette: {
//     primary: {
//       light: StyleguideVandewieleColors.tints.blue[60],
//       main: StyleguideVandewieleColors.primary.blue,
//       dark: StyleguideVandewieleColors.tints.black[80],
//       contrastText: "#ffffff",
//     },
//     secondary: {
//       light: "#4974b7",
//       main: StyleguideVandewieleColors.primary.blue,
//       dark: "#002259",
//       contrastText: "#FFFFFF",
//     },
//     info: {
//       light: "#485cb2",
//       main: "#003382",
//       dark: "#000f54",
//       contrastText: "#ffffff",
//     },
//     text: {
//       primary: StyleguideVandewieleColors.misc.colorTextBlack,
//     },
//     background: {
//       default: StyleguideVandewieleColors.otherColors.backgroundSite,
//     },
//   },
//   shape: {
//     borderRadius: 10,
//   },
//   typography: {
//     fontFamily: ["'KoneFont'", "Sans-Serif"].join(","),
//     fontSize: 14,
//     // useNextVariants: true,
//     // suppressDeprecationWarnings: true
//   },
// };

// const sidelLightTheme = createTheme({
//   ...defaultOptions,
//   breakpoints: {
//     values: {
//       xs: 0,
//       sm: 600,
//       md: 1024,
//       lg: 1280,
//       xl: 1920,
//     },
//   },
//   palette: {
//     ...defaultOptions.palette,
//     mode: "light",
//     action: {
//       active: "#000000",
//     },
//   },
// });

export const themeSettings = {
  main: savioTheme,
  // header: savioTheme,
  // drawer: darkTheme,
  // footer: darkTheme,
  // colors: {
  //   cartBadge: {
  //     color: "#FFFFFF",
  //     backgroundColor: StyleguideVandewieleColors.accent.red,
  //     border: "0.5px solid #FFFFFF",
  //   },
  //   chartColors: chartColors,
  //   chartLabels: {
  //     borderColor: "#000f54",
  //     backgroundColor: "#000f54",
  //     color: "#FFFFFF",
  //   },
  //   othersColor: othersColor,
  //   kone: {
  //     blue: "var(--blueKone-color)",
  //     dark: "#3B4245",
  //     celeste: StyleguideVandewieleColors.tints.blue[20], //'#eaf7ff',
  //   },
  // },
  // fonts: {
  //   principal: "KoneFont",
  //   secondFont: "Inter",
  //   // secondFont: "KoneFont",
  //   bold: 600,
  // },
};
